import { useCallback, useMemo } from 'react'

import { TreeNode } from './types'

type CurrentPosition = {
  buildingIndex: number
  floorIndex: number
}

type UseFloorNavigationProps = {
  tree: TreeNode[]
  mapUuid: string | null
  onMapUuidChange: (uuid: string) => void
}

type UseFloorNavigationReturn = {
  onFloorPrev: () => void
  onFloorNext: () => void
  currentPosition: CurrentPosition | null
  canNavigate: boolean
}

const findCurrentPosition = (tree: TreeNode[], mapUuid: string | null): CurrentPosition | null => {
  if (!mapUuid || tree.length === 0) return null

  for (let buildingIndex = 0; buildingIndex < tree.length; buildingIndex++) {
    const floorIndex = tree[buildingIndex].children.findIndex(({ id }) => id === mapUuid)
    if (floorIndex !== -1) {
      return { buildingIndex, floorIndex }
    }
  }

  return null
}

export const useFloorNavigation = ({
  tree,
  mapUuid,
  onMapUuidChange,
}: UseFloorNavigationProps): UseFloorNavigationReturn => {
  const currentPosition = useMemo(
    () => findCurrentPosition(tree, mapUuid),
    [tree, mapUuid]
  )

  const canNavigate = currentPosition !== null

  const handleFloorNext = useCallback(() => {
    if (!currentPosition) return

    const { buildingIndex, floorIndex } = currentPosition
    const currentBuilding = tree[buildingIndex]
    const isLastFloorInBuilding = floorIndex === currentBuilding.children.length - 1

    if (!isLastFloorInBuilding) {
      // Aller à l'étage suivant du même bâtiment
      const nextFloor = currentBuilding.children[floorIndex + 1]
      onMapUuidChange(nextFloor.id)
      return
    }

    // Aller au premier étage du bâtiment suivant (ou revenir au premier bâtiment)
    const nextBuildingIndex = (buildingIndex + 1) % tree.length
    const nextBuilding = tree[nextBuildingIndex]

    if (nextBuilding.children.length > 0) {
      onMapUuidChange(nextBuilding.children[0].id)
    }
  }, [currentPosition, tree, onMapUuidChange])

  const handleFloorPrev = useCallback(() => {
    if (!currentPosition) return

    const { buildingIndex, floorIndex } = currentPosition
    const isFirstFloorInBuilding = floorIndex === 0

    if (!isFirstFloorInBuilding) {
      // Aller à l'étage précédent du même bâtiment
      const prevFloor = tree[buildingIndex].children[floorIndex - 1]
      onMapUuidChange(prevFloor.id)
      return
    }

    // Aller au dernier étage du bâtiment précédent (ou revenir au dernier bâtiment)
    const prevBuildingIndex = (buildingIndex - 1 + tree.length) % tree.length
    const prevBuilding = tree[prevBuildingIndex]

    if (prevBuilding.children.length > 0) {
      onMapUuidChange(prevBuilding.children[prevBuilding.children.length - 1].id)
    }
  }, [currentPosition, tree, onMapUuidChange])

  return {
    onFloorPrev: handleFloorPrev,
    onFloorNext: handleFloorNext,
    currentPosition,
    canNavigate,
  }
}

